// =====================================================================
// Eventi sul bus RabbitMQ. Producer e consumer (backend monolite, economic-engine,
// futuri oicp-gateway/sella-bridge) importano da qui per evitare drift.
// =====================================================================

import type { OicpCpoEvseStatusRecoveryRequest } from '../oicp-cpo-recovery.interface';

export interface BaseEvent {
  eventId: string;
  eventVersion: number;
  occurredAt: string;       // ISO 8601
  source: string;           // 'backend' | 'economic-engine' | ...
}

// ---- session.ended ---------------------------------------------------
export interface SessionEndedEvent extends BaseEvent {
  sessionId: string;
  // Audience del rollout (ROLLOUT_PAYMENTS_ENGINE): l'engine non conosce gli
  // utenti, quindi isAdmin viaggia nell'evento. Si propaga in cdr.ready →
  // charge-cdr, dove gate l'incasso reale. undefined su eventi legacy = false.
  isAdmin?: boolean;
}

// ---- cdr.ready (emesso dall'economic-engine quando il CDR è elaborato) ----
export interface CdrReadyEvent extends BaseEvent {
  cdrId: string;
  sessionId: string;
  userId: string | null;
  sessionType: 'B2C_NATIVE' | 'OCPI_ROAMING' | 'OICP_ROAMING' | 'UNKNOWN';
  totalCost: { exclVat: number; inclVat: number; currency: string };
  totalEnergyKwh: number;
  startedAt: string;
  endedAt: string;
  operatorId: string | null;
  connectorId: number | null;
  sponsorshipPolicyId: number | null;
  // Propagato da session.ended. Non è più un gate di rilascio (l'incasso engine è GA per tutti):
  // serve al canary admin sull'IMPORTO (ADMIN_FLAT_CHARGE_EUR) e all'audit.
  isAdmin?: boolean;
}

// ---- payment.charged (emesso dopo charge attempt sul PSP) ----
export interface PaymentChargedEvent extends BaseEvent {
  cdrId: string;
  sessionId: string;
  userId: string | null;
  amount: { exclVat: number; inclVat: number; currency: string };
  gateway: 'SELLA' | 'STRIPE' | 'SHADOW';
  status: 'COMPLETED' | 'FAILED' | 'SKIPPED';
  externalTransactionId: string | null;
  error: string | null;
  /**
   * Id della riga `bank_transaction` scritta dall'engine (fase authoritative):
   * il listener backend la porta a Completed/Failed riusando il fan-out notifiche.
   * Assente in shadow / fase 1.
   */
  bankTransactionId?: number | null;
  /**
   * true = pagamento SIMULATO (utente in DRY_RUN_*): nessuna chiamata al PSP,
   * la riga bank_transaction è flaggata dryRun. I downstream (invoicing) skippano
   * il SOAP reale. Assente/false = incasso reale.
   */
  dryRun?: boolean;
}

// ---- cdr.pushed-to-oicp (status finale dopo che oicp-gateway ha completato) ----
export interface CdrPushedToOicpEvent extends BaseEvent {
  cdrId: string;
  oicpPartnerId: string | null;
  status: 'PUSHED' | 'FAILED' | 'SHADOW' | 'REQUESTED';
  externalReference: string | null;
  error: string | null;
}

// ---- oicp.cdr-push.requested ----
// economic-engine pubblica → oicp-gateway consuma → fa push reale verso Hubject
// `cdr` è il payload canonical (lower-camel) richiesto dal mapper outbound CPO 2.3.
// Il gateway non costruisce nulla: passa `cdr` al CpoOutboundService.
export interface OicpCdrPushCanonicalIdentification {
  rfidMifareFamilyIdentification?: { uid: string };
  qrCodeIdentification?: { evcoId: string; hashedPin?: unknown; pin?: string };
  plugAndChargeIdentification?: { evcoId: string };
  remoteIdentification?: { evcoId: string };
  rfidIdentification?: {
    evcoId: string;
    expiryDate?: string;
    printedNumber?: string;
    rfid?: string;
    uid?: string;
  };
}

export interface OicpCdrPushCanonical {
  operatorId?: string;
  sessionId: string;
  cpoPartnerSessionId?: string;
  empPartnerSessionId?: string;
  partnerProductId?: string;
  evseId: string;
  identification: OicpCdrPushCanonicalIdentification;
  chargingStart: string;
  chargingEnd: string;
  sessionStart: string;
  sessionEnd: string;
  meterValueStart?: number;
  meterValueEnd?: number;
  meterValuesInBetween?: number[];
  consumedEnergy: number;
  signedMeteringValues?: unknown;
  calibrationLawVerificationInfo?: unknown;
  hubOperatorId?: string;
  hubProviderId?: string;
}

export interface OicpCdrPushRequestedEvent extends BaseEvent {
  cdrId: string;
  cdr: OicpCdrPushCanonical;
}

// ---- oicp.evse-data.observed / oicp.evse-status.observed ---------------
// (oicp-gateway → backend) Dati/stati EVSE roaming osservati dal pull Hubject
// (noi EMP). Sostituisce il trasporto WS+outbox per questi tipi canonici.
// `payload` è il payload canonico INVARIATO che prima viaggiava nell'envelope
// WS: il backend lo passa a EmpEvseIngestionQueueService.handleEvseData/Status
// esattamente come faceva il router WS — coalescing/retry restano nella coda
// Redis interna del backend.
export interface OicpEvseObservedEvent extends BaseEvent {
  canonicalType: string;              // es. 'oicp.emp.pull-evse-status'
  messageId: string;                  // messageId dell'envelope canonico
  correlationId?: string;
  gatewayId?: number;                 // id del gateway lato backend (da handshake WS), best-effort
  payload: Record<string, unknown>;   // payload canonico verbatim
}

// ---- oicp.evse-status.push.requested (backend → oicp-gateway) ----------
// Comando fire-and-forget: pusha lo stato di un NOSTRO EVSE (noi CPO) verso
// Hubject. Sostituisce il comando WS sincrono PushEvseStatus: validazione,
// suppression durante recovery, 603 → oicp.evse.recovery.requested e metriche
// vivono nel gateway (GatewayCommandService) — il backend non aspetta l'ack.
export interface OicpEvseStatusPushRequestedEvent extends BaseEvent {
  operatorId: string;
  evseId: string;
  evseStatus: string;
  actionType: string;          // es. 'update'
  correlationId?: string;
}

// ---- oicp.evse.recovery.requested (oicp-gateway → backend) -------------
// Hubject ha risposto 603 "unknown EVSE" a un PushEvseStatus: il gateway
// chiede al backend un full-load PushEvseData di recupero per l'operatore.
// RMQ è SOLO trasporto: delay (30 min) e coalescing per-operatore restano
// nella coda Redis interna del backend (OICP_CPO_EVSE_STATUS_RECOVERY_QUEUE),
// consumata da OicpCpoEvseStatusRecoveryService → OicpCpoFullLoadService.
export interface OicpEvseRecoveryRequestedEvent extends BaseEvent {
  request: OicpCpoEvseStatusRecoveryRequest;
}

// Nota: l'addebito trust-based mid-session è stato RIMOSSO del tutto (con la
// pre-auth — hold all'avvio + capture a fine sessione — non serve più): sia il
// flusso engine sia il trust-billing legacy del backend sono stati eliminati.
// Rimossi quindi anche gli eventi `meter-value.observed` e `mid-session.charged`.

// ---- pre-authorization (hold) — modello distributore ------------------
// Il backend pubblica preauth.requested all'avvio sessione e ASPETTA l'esito
// prima di far partire la ricarica (gate "no hold, no charge"). L'engine
// autorizza sul PSP (CHIAVE = sessionId: il cdrId non esiste ancora) e risponde
// authorized/failed. L'INCASSO avviene a cdr.ready (riuso PaymentChargedEvent):
// importo reale, oppure VOID se il costo finale è 0.
export interface PreauthRequestedEvent extends BaseEvent {
  sessionId: string;
  userId: string | null;
  amount: { exclVat: number; inclVat: number; currency: string }; // cap dell'hold
  // Non è più un gate di rilascio (il pre-auth è GA per tutti): resta come informazione di contesto
  // per il canary admin sull'importo e per l'audit.
  isAdmin?: boolean;
  /**
   * Hold GIÀ autorizzato fuori dall'engine: è il caso wallet/CIT, dove l'utente
   * ha autenticato il pagamento sulla pagina hosted del PSP e l'autorizzazione
   * esiste prima che la sessione nasca. L'engine NON chiama il PSP: registra
   * l'hold con questo identificativo e risponde authorized. Capture, void e
   * sweep restano identici (stesso `payment_hold`, stesso paymentId REST).
   *
   * Il backend DEVE aver verificato lato server (getPaymentDetail) che
   * l'autorizzazione sia reale, dell'utente giusto e di importo sufficiente:
   * qui dentro non si rivalida.
   */
  existingAuthorization?: {
    psp: 'SELLA' | 'STRIPE';
    /** paymentId REST Payment API: lo stesso che capture/void useranno. */
    paymentId: string;
    /** come è stata autenticata: informativo, per audit e reportistica. */
    method?: 'APPLE_PAY' | 'GOOGLE_PAY' | 'CARD_HOSTED';
  };
}

export interface PreauthAuthorizedEvent extends BaseEvent {
  sessionId: string;
  userId: string | null;
  authorizedAmount: { exclVat: number; inclVat: number; currency: string };
  gateway: 'SELLA' | 'STRIPE' | 'SHADOW';
  status: 'AUTHORIZED' | 'SKIPPED'; // SKIPPED = shadow (nessun hold reale, sessione non bloccata)
  externalTransactionId: string | null;
}

export interface PreauthFailedEvent extends BaseEvent {
  sessionId: string;
  userId: string | null;
  reason: string;
}

/**
 * Comando (backend → engine, fire-and-forget): rilascia un hold pre-auth (void
 * dell'auth non ancora incassata). Due trigger: (a) gate in timeout — hold
 * autorizzato tardi mentre la sessione è già stata annullata; (b) azione operatore
 * dal backoffice. L'engine riusa `voidAuthorization` + `markReleased` (stessa logica
 * dello sweep). Idempotente per sessionId.
 */
export interface PreauthReleaseRequestedEvent extends BaseEvent {
  sessionId: string;
  reason: string; // es. 'gate-timeout' | 'operator:{userId}'
}

/**
 * Comando (backend → engine, fire-and-forget): addebita (capture) un hold ACTIVE
 * per un importo scelto dall'operatore, 0 <= amount <= importo autorizzato del
 * hold. amount=0 è la "conferma addebito a 0": chiude l'hold senza incassare,
 * utile quando il void PSP (callDeleteS2S) non è disponibile per lo stato della
 * transazione (es. errore Sella 8008 "Funzione non disponibile"). L'engine chiama
 * callSettleS2S e, su conferma, marca l'hold CAPTURED con capturedAmount=amount.
 * Idempotente per sessionId.
 */
export interface PreauthCaptureRequestedEvent extends BaseEvent {
  sessionId: string;
  amount: number;
  reason: string; // es. 'operator:{userId}'
}

/**
 * Comando (backend → engine, fire-and-forget): rimborsa (totale o parziale) un
 * pagamento GIÀ incassato, triggerato da un operatore dal backoffice. L'engine chiama
 * il refund PSP (Sella `callRefundS2S`) e scrive una riga `bank_transaction`
 * compensativa a valore negativo. Idempotente per (bankTransactionId).
 */
/**
 * Annulla un'autorizzazione PSP che non ha una sessione (oggi: i 0,01 € della
 * verifica carta). Le chiusure le fa l'engine, sempre — il backend pubblica e
 * basta. Fire-and-forget: nessuno aspetta l'esito.
 */
export interface PaymentCancelRequestedEvent extends BaseEvent {
  /** id REST Payment API dell'autorizzazione da annullare. */
  paymentId: string;
  psp: 'SELLA' | 'STRIPE';
  /** solo audit/log, es. 'registration-verification-release'. */
  reason: string;
  userId: string | null;
}

/**
 * Apertura di un hold pagato dall'utente sulla pagina hosted del PSP.
 * L'utente sta aspettando: una sola andata/ritorno, risposta su
 * `hostedHoldCreatedRoutingKey(correlationId)`.
 */
export interface HostedHoldRequestedEvent extends BaseEvent {
  /** generato dal backend: è la chiave con cui tornerà la risposta. */
  correlationId: string;
  userId: string;
  amount: { inclVat: number; currency: string };
  buyer?: { email?: string; name?: string };
  /** dove il PSP rimanda l'utente a pagamento fatto. */
  returnUrls: { buyerOK: string; buyerKO: string };
  /**
   * Già costruito dal backend e **entro 50 caratteri** (oltre, Axerve risponde
   * `1116 troppo lungo`). L'engine lo usa così com'è: non lo ricostruisce.
   */
  shopTransactionId: string;
}

export interface HostedHoldCreatedEvent extends BaseEvent {
  correlationId: string;
  ok: boolean;
  /** id da usare poi per verify/capture/void. Null se `ok` è false. */
  paymentId: string | null;
  /** link della pagina hosted da aprire. Null se `ok` è false. */
  href: string | null;
  error: string | null;
}

/**
 * Link di pagamento per un INSOLUTO: una `bank_transaction` rimasta non incassata
 * (sessione consumata e mai pagata). L'operatore lo genera dal backoffice e lo
 * manda al cliente; l'engine crea l'autorizzazione sulla pagina hosted e risponde
 * col link su `paymentLinkCreatedRoutingKey(correlationId)`.
 */
export interface PaymentLinkRequestedEvent extends BaseEvent {
  /** generato dal backend: è la chiave con cui tornerà la risposta. */
  correlationId: string;
  /** la riga di ledger che questo pagamento dovrà chiudere. */
  bankTransactionId: number;
  userId: string | null;
  /** dovuto residuo, che è anche l'importo del link. */
  amount: { inclVat: number; currency: string };
  buyer?: { email?: string; name?: string };
  /** dove il PSP rimanda il cliente a pagamento fatto. */
  returnUrls: { buyerOK: string; buyerKO: string };
  /**
   * Già costruito dal backend e **entro 50 caratteri** (oltre, Axerve risponde
   * `1116 troppo lungo`). L'engine lo usa così com'è: non lo ricostruisce.
   */
  shopTransactionId: string;
  /** solo audit, es. 'operator:{userId}'. */
  reason: string;
}

export interface PaymentLinkCreatedEvent extends BaseEvent {
  correlationId: string;
  ok: boolean;
  /** id del pagamento da mandare in capture quando il cliente avrà pagato. Null se `ok` è false. */
  paymentId: string | null;
  /** link da consegnare al cliente (email/QR). Null se `ok` è false. */
  href: string | null;
  error: string | null;
}

/**
 * Il cliente ha pagato il link. Con la **Separazione M.O.T.O.** attiva sul profilo
 * quel pagamento nasce `AUT` (autorizzato) e NON incassato: per prendere i soldi
 * serve una capture, che è dell'engine — insieme alla chiusura della riga di ledger.
 *
 * Il backend lo pubblica quando riceve la notify S2S del PSP; il fatto che il
 * cliente abbia pagato NON si deduce dal body della notify (endpoint pubblico) ma
 * si richiede al PSP, e l'engine lo ri-verifica prima di incassare.
 *
 * Fire-and-forget, idempotente per paymentId.
 */
export interface PaymentLinkCaptureRequestedEvent extends BaseEvent {
  /** autorizzazione aperta dal link, da incassare. */
  paymentId: string;
  /** riga di ledger da chiudere: senza, l'incasso non saprebbe quale debito salda. */
  bankTransactionId: number;
  /** dovuto da incassare. L'engine NON incassa se l'autorizzato non lo copre. */
  amount: { inclVat: number; currency: string };
  shopTransactionId: string;
  reason: string;
}

export interface RefundRequestedEvent extends BaseEvent {
  bankTransactionId: number | null;                // riga BALANCE originale; null = rimborso di un HOLD (nessuna BT, es. M.O.T.O. off)
  cdrId: string | null;
  sessionId: string | null;
  paymentId: string | null;                        // externalTransactionId Sella dell'incasso originale
  shopTransactionId: string;                       // chiave shopTransID usata al settle (cdrId o sessionId)
  amount: { inclVat: number; currency: string };   // importo da rimborsare (<= incassato)
  reason: string;                                  // es. 'operator:{userId}:{motivo}'
}

// =====================================================================
// Notification-service — eventi su exchange `notification.events`.
// Il backend (unico a conoscere il PERCHÉ) pubblica COSA inviare e A CHI;
// il notification-service esegue (push FCM, email SMTP) in modo idempotente.
// =====================================================================

/**
 * - TRANSACTIONAL: notifiche economiche (pagamenti, addebiti, esiti) —
 *   SEMPRE consegnate, nessuna preferenza le disattiva (vincolo legale:
 *   l'utente va avvisato di ogni movimento economico).
 * - PERSONAL: attività personale non economica (inizio/stato/fine ricarica,
 *   prenotazioni) — disattivabile con personalPush/EmailEnabled.
 * - SERVICE: comunicazioni operative broadcast (manutenzioni, avvisi rete).
 * - MARKETING: promozionale, opt-in GDPR.
 */
export type NotificationCategory = 'TRANSACTIONAL' | 'PERSONAL' | 'SERVICE' | 'MARKETING';

// Come il producer vuole che il messaggio venga consegnato. La scelta
// effettiva del canale (escalation a email per device stantii) resta
// responsabilità del notification-service.
export type ChannelPlan = 'PUSH_WITH_EMAIL_FALLBACK' | 'PUSH_ONLY' | 'EMAIL_ONLY';

// Chiavi dei template email posseduti dal notification-service.
// Il backend NON renderizza: passa templateKey + data.
export type EmailTemplateKey =
  | 'auth.password-reset'
  | 'auth.password-changed'
  | 'auth.register'
  | 'auth.admin-password-reset'
  | 'auth.admin-register'
  | 'auth.changed-my-password'
  // Sollecito di un insoluto: porta il link di pagamento del PSP. Chiave DEDICATA
  // e non `generic.notification` perché una richiesta di soldi con l'aspetto di un
  // avviso generico è esattamente ciò che il cliente scambia per phishing.
  // `data`: { payUrl, amount, currency, chargeDate, chargeRef }.
  | 'payment.link-reminder'
  | 'generic.notification';

// ---- notification.requested (backend → notification-service) ---------
// Una notifica a UN utente. title/body arrivano GIÀ localizzati dal
// producer (il backend possiede TranslateService e User.lang); il service
// resta agnostico. Il service: dedup (messageId first-wins, collapseKey
// last-wins su finestra), risolve i device, decide push vs email,
// persiste la riga `notification` (lista in-app) e il dispatch ledger.
export interface NotificationRequestedEvent extends BaseEvent {
  messageId: string;            // uuid — chiave idempotenza first-wins
  userId: string;
  category: NotificationCategory;
  channelPlan: ChannelPlan;
  title: string;                // già localizzato dal producer
  body: string;                 // già localizzato dal producer
  data?: Record<string, string>;          // deep link (notification_uid, sessionId, …)
  collapseKey?: string;                   // es. `session-update:${sessionId}` — last-wins
  collapseWindowMs?: number;              // default 30_000, per-messaggio
  emailTemplateKey?: EmailTemplateKey;    // default 'generic.notification' per il fallback
  langOverride?: 'it' | 'en';
}

// ---- email.requested (backend → notification-service) ----------------
// "Manda questa email": transazionali pure (auth), nessuna riga in-app,
// nessuna logica device, nessun check consenso (legittimo interesse).
// `to.email` diretta per i flussi pre-login (forgot password).
// `data` contiene le variabili del template (actionUid è SEMPRE generato
// dal backend — il service non possiede il dominio actions).
export interface EmailRequestedEvent extends BaseEvent {
  messageId: string;            // uuid — chiave idempotenza first-wins
  to: { userId: string } | { email: string };
  templateKey: EmailTemplateKey;
  data: Record<string, unknown>;
  lang: 'it' | 'en';
  attachments?: Array<{ filename: string; contentBase64: string; contentType: string }>;
  /**
   * true = invio SEMPRE, anche con SMTP_DRY_RUN=true. Per alert operativi
   * INTERNI (es. corrispettivo/fattura falliti verso gli admin), che non devono
   * mai essere zittiti dalla modalità dry-run e non impattano i clienti.
   */
  forceSend?: boolean;
}

// ---- device.token-invalidated (notification-service → backend) -------
// FCM ha risposto con un errore permanente sul token (unregistered,
// invalid-registration-token): il backend revoca la riga user_device e
// pulisce l'eventuale Client.wpEndpoint legacy. Il service NON scrive
// le tabelle device (owner: backend).
export interface DeviceTokenInvalidatedEvent extends BaseEvent {
  fcmToken: string;
  userId: string | null;
  reason: string;               // codice errore FCM
}

// ---- FCM topics --------------------------------------------------------
// Subscribe/unsubscribe avviene CLIENT-SIDE (plugin @capacitor-community/fcm)
// pilotato dalle preferenze utente salvate nel backend. common_library è
// dipendenza anche di frontend_mobile: unica source of truth dei nomi topic.
export const FCM_TOPICS = {
  service: { en: 'yf-service-en', it: 'yf-service-it' },
  marketing: { en: 'yf-marketing-en', it: 'yf-marketing-it' },
} as const;

export type FcmTopicCategory = keyof typeof FCM_TOPICS;
export type FcmTopicLanguage = keyof (typeof FCM_TOPICS)['service'];
